import { Stack, Theme, useMediaQuery, styled } from '@mui/material';
import { RadioButtonGroup, RadioButtonGroupProps } from '@chr/eds-react';

const TenantSelectorStack = styled(Stack)<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  backgroundColor: '#F6F7F8',
  padding: '0.5rem',
  marginBottom: '1rem',
  borderRadius: '1rem',
  '& > div': {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
    gridAutoFlow: 'row',
    gap: '1rem',
  },
}));

export interface TenantSelectorProps {
  tenants: string[];
  selectedTenant: string;
  onTenantChange: (tenant: string) => void;
}

/** EDS 2 RadioButtonGroup now requires at least two buttons 
 * This helper function converts a list of tenant strings into a tuple of RadioButtonProps that satisfies the new type.
 * https://github.com/ch-robinson-internal/ui-platform/blob/55a1ed93a92da4ce8ebceebb7f07d87b2f2a131a/docs/migration/Updates.md#update-buttons-prop
*/
function toRadioButtons(
  tenants: string[],
  onTenantChange: (tenant: string) => void
): RadioButtonGroupProps['buttons'] {
  const [first, second, ...rest] = tenants;

  return [
    {
      value: first,
      label: first,
      'aria-label': first,
      onChange: () => onTenantChange(first),
    },
    {
      value: second,
      label: second,
      'aria-label': second,
      onChange: () => onTenantChange(second),
    },
    ...rest.map((tenant) => ({
      value: tenant,
      label: tenant,
      'aria-label': tenant,
      onChange: () => onTenantChange(tenant),
    })),
  ];
}

export const TenantSelector = ({ tenants, selectedTenant, onTenantChange }: TenantSelectorProps) => {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTenantChange(e.target.value);
  };

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  if (tenants.length <= 1) {
    return null;
  }

  const radioButtons = toRadioButtons(tenants, onTenantChange);

  return (
    <TenantSelectorStack isMobile={isMobileScreen}>
      <RadioButtonGroup
        name="radioGroup"
        direction="row"
        value={selectedTenant}
        onChange={onChange}
        buttons={radioButtons}
      />
    </TenantSelectorStack>
  );
};
